import { useState } from 'react';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const answers = [
  {
    question: 'What does Green World Construction do?',
    answer: 'Green World Construction and Roofing is a full-service roofing and exterior construction company based in Chicago, IL. We install, repair, and replace all types of roofing systems for residential and commercial properties throughout Chicago and 24+ Chicagoland suburbs. Services include flat roofing, shingle roofing, commercial roofing, tuckpointing, masonry, chimney repair, sheet metal fabrication, vinyl siding, and sealcoating.',
    icon: 'ri-building-line',
  },
  {
    question: 'Who does Green World serve?',
    answer: "We serve homeowners, property managers, landlords, and business owners across Chicago and the greater Chicagoland area. Our team handles everything from single-family homes and two-flats to apartment buildings, retail properties, and commercial facilities. We've been the trusted choice for Chicago-area roofing since 1989.",
    icon: 'ri-user-star-line',
  },
  {
    question: 'What are the benefits of hiring Green World?',
    answer: 'Clients benefit from over 35 years of Chicago-specific roofing expertise, a family-owned team with direct accountability, free written estimates with no surprise costs, use of premium materials rated for Midwest climate conditions, and a thorough final walkthrough. We keep customers informed from the first inspection through project completion.',
    icon: 'ri-award-line',
  },
  {
    question: 'What is the process for getting a new roof?',
    answer: 'Our process starts with a free on-site inspection and assessment. We then provide a clear written estimate with scope, materials, and timeline. Once approved, our crew handles all installation with no subcontractors. After completion, we walk through the project with you to ensure every detail meets our standards before we consider the job done.',
    icon: 'ri-map-pin-time-line',
  },
];

export default function AIAnswerBlock() {
  const [active, setActive] = useState<number | null>(0);

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-brand-cream rounded-full translate-x-1/2 -translate-y-1/2 opacity-60" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-green/5 rounded-full -translate-x-1/2 translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: header + intro */}
          <RevealOnScroll direction="left">
            <span className="text-brand-gold text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
              <span className="w-8 h-px bg-brand-gold inline-block" />
              About Green World
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-brand-dark mt-4 mb-6 leading-tight">
              Chicago&apos;s Roofing &amp;<br />
              <span className="text-brand-green italic">Construction Authority</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-5">
              Green World Construction is family owned and operated. We have been serving Chicago and the
              Chicagoland suburbs since 1989. Current owner Wayne Christo continues the family legacy started
              by his father — building a reputation for quality, transparency, and lasting results.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              We started with flat roofs and now specialize in all types of roofing systems: shingles,
              tuckpointing, masonry, asphalt paving, customized sheet metal, concrete, and general exterior
              contracting. Every project receives a complete inspection, a written estimate, and direct
              communication from start to finish.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 border-t border-brand-cream-3 pt-6">
              {[
                { val: '1989', label: 'Founded in Chicago' },
                { val: '5,000+', label: 'Projects Completed' },
                { val: '24+', label: 'Cities Served' },
              ].map((stat) => (
                <div key={stat.label} className="text-center group">
                  <div className="font-display text-2xl lg:text-3xl font-bold text-brand-green group-hover:text-brand-gold transition-colors">
                    {stat.val}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-tight">{stat.label}</div>
                </div>
              ))}
            </div>
          </RevealOnScroll>

          {/* Right: Q&A accordion */}
          <RevealOnScroll direction="right" delay={100}>
            <div className="flex flex-col gap-3">
              <div className="text-xs font-bold text-brand-gold uppercase tracking-widest mb-2 flex items-center gap-2">
                <i className="ri-question-answer-line" />
                Common Questions Answered
              </div>
              {answers.map((item, idx) => (
                <div
                  key={idx}
                  className="border border-brand-cream-3 rounded-xl overflow-hidden transition-all duration-200 hover:border-brand-green/30"
                >
                  <button
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-brand-cream/60 transition-colors"
                    onClick={() => setActive(active === idx ? null : idx)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${active === idx ? 'bg-brand-green text-white' : 'bg-brand-green/10'}`}>
                        <i className={`${item.icon} text-sm ${active === idx ? 'text-white' : 'text-brand-green'}`} />
                      </div>
                      <span className="text-sm font-semibold text-brand-dark">{item.question}</span>
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 bg-brand-cream rounded-full">
                      <i className={`text-brand-green text-sm ${active === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </div>
                  </button>
                  {active === idx && (
                    <div className="px-5 pb-5 pt-1 text-sm text-gray-600 leading-relaxed border-t border-brand-cream-2 bg-brand-cream/30">
                      <div className="pt-3">{item.answer}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}
